/**
 * Binary bitmap, one byte per pixel: 0 = ink (black), 255 = background (white).
 *
 * The convention is inherited from PIL so the port stays verifiable against
 * `img2cricut.py`. MIND THE POLARITY: over black ink (=0), a MAXIMUM filter
 * **erodes** the ink and a MINIMUM one **dilates** it. That is the reverse of
 * the intuition, and it is the number one source of bugs when porting
 * morphology.
 */
export interface Bitmap {
  readonly width: number;
  readonly height: number;
  /** length === width * height; holds only 0 or 255 */
  readonly data: Uint8Array;
}

export const INK = 0;
export const BG = 255;

export function createBitmap(width: number, height: number, fill = BG): Bitmap {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
    throw new RangeError(`invalid dimensions: ${width}x${height}`);
  }
  const data = new Uint8Array(width * height);
  if (fill !== 0) data.fill(fill);
  return { width, height, data };
}

/** Thresholds grey to binary. Same as PIL `point(v => v < threshold ? 0 : 255)`. */
export function binarize(
  gray: Uint8Array | Uint8ClampedArray,
  width: number,
  height: number,
  threshold = 128,
): Bitmap {
  if (gray.length !== width * height) {
    throw new RangeError(`gray tiene ${gray.length} px, se esperaban ${width * height}`);
  }
  const data = new Uint8Array(width * height);
  for (let i = 0; i < data.length; i++) data[i] = gray[i] < threshold ? INK : BG;
  return { width, height, data };
}

/** Converts RGBA (Canvas ImageData) to grey with Rec.601 luma, composited over white. */
export function rgbaToGray(rgba: Uint8ClampedArray, width: number, height: number): Uint8Array {
  const out = new Uint8Array(width * height);
  for (let i = 0, p = 0; i < out.length; i++, p += 4) {
    const a = rgba[p + 3] / 255;
    // composite over white: a transparent PNG must read as background, not ink
    const r = rgba[p] * a + 255 * (1 - a);
    const g = rgba[p + 1] * a + 255 * (1 - a);
    const b = rgba[p + 2] * a + 255 * (1 - a);
    out[i] = (0.299 * r + 0.587 * g + 0.114 * b) | 0;
  }
  return out;
}

export function countInk(bm: Bitmap): number {
  let n = 0;
  for (let i = 0; i < bm.data.length; i++) if (bm.data[i] === INK) n++;
  return n;
}

export interface BBox { x0: number; y0: number; x1: number; y1: number }

/** Smallest box containing all the ink. `null` if there is none. Ends exclusive. */
export function inkBBox(bm: Bitmap): BBox | null {
  const { width, height, data } = bm;
  let x0 = width, y0 = height, x1 = -1, y1 = -1;
  for (let y = 0; y < height; y++) {
    const row = y * width;
    for (let x = 0; x < width; x++) {
      if (data[row + x] === INK) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  return x1 < 0 ? null : { x0, y0, x1: x1 + 1, y1: y1 + 1 };
}

export function crop(bm: Bitmap, box: BBox): Bitmap {
  const w = box.x1 - box.x0, h = box.y1 - box.y0;
  if (w <= 0 || h <= 0) throw new RangeError("empty crop");
  const out = createBitmap(w, h);
  for (let y = 0; y < h; y++) {
    const src = (box.y0 + y) * bm.width + box.x0;
    out.data.set(bm.data.subarray(src, src + w), y * w);
  }
  return out;
}

/** Surrounds the bitmap with `margin` px of background on every side. */
export function pad(bm: Bitmap, margin: number): Bitmap {
  if (margin <= 0) return bm;
  const w = bm.width + 2 * margin;
  const out = createBitmap(w, bm.height + 2 * margin);
  for (let y = 0; y < bm.height; y++) {
    const src = y * bm.width;
    out.data.set(bm.data.subarray(src, src + bm.width), (y + margin) * w + margin);
  }
  return out;
}

/** Crops to the ink bbox leaving `margin` px of air. Returns null if there is no ink. */
export function trimToInk(bm: Bitmap, margin = 16): Bitmap | null {
  const bb = inkBBox(bm);
  if (!bb) return null;
  return crop(bm, {
    x0: Math.max(0, bb.x0 - margin),
    y0: Math.max(0, bb.y0 - margin),
    x1: Math.min(bm.width, bb.x1 + margin),
    y1: Math.min(bm.height, bb.y1 + margin),
  });
}

/** Paints a background rectangle -- the "eraser" for dropping a word without regenerating. */
export function eraseRect(bm: Bitmap, box: BBox): Bitmap {
  const out: Bitmap = { width: bm.width, height: bm.height, data: Uint8Array.from(bm.data) };
  const x0 = Math.max(0, box.x0), x1 = Math.min(bm.width, box.x1);
  const y0 = Math.max(0, box.y0), y1 = Math.min(bm.height, box.y1);
  for (let y = y0; y < y1; y++) out.data.fill(BG, y * bm.width + x0, y * bm.width + x1);
  return out;
}

/** Rotates in multiples of 90°. COUNTER-CLOCKWISE, same as `PIL.Image.rotate`. */
export function rotate(bm: Bitmap, degrees: 0 | 90 | 180 | 270): Bitmap {
  if (degrees === 0) return bm;
  const { width: w, height: h, data } = bm;
  const swap = degrees !== 180;
  const out = createBitmap(swap ? h : w, swap ? w : h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const v = data[y * w + x];
      let nx: number, ny: number;
      if (degrees === 90) { nx = y; ny = w - 1 - x; }
      else if (degrees === 180) { nx = w - 1 - x; ny = h - 1 - y; }
      else { nx = h - 1 - y; ny = x; }
      out.data[ny * out.width + nx] = v;
    }
  }
  return out;
}
